<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="robot"/> AI Trợ lý - Tổng quan</h1>
    </div>

    <div class="row mb-3">
      <div class="col-md-4">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filterFrom"/>
        </b-form-group>
      </div>
      <div class="col-md-4">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filterTo"/>
        </b-form-group>
      </div>
      <div class="col-md-4 d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3">
          <font-awesome-icon icon="search"/> Xem
        </b-button>
      </div>
    </div>

    <!-- Alert banner -->
    <b-alert v-if="alert && alert.isAlert" variant="danger" show dismissible class="mb-4">
      <h5 class="mb-1"><font-awesome-icon icon="exclamation-triangle"/> Cảnh báo: Tỉ lệ lỗi cao!</h5>
      <p class="mb-1">Trong 1 giờ qua: <strong>{{ alert.error }}/{{ alert.total }}</strong> câu hỏi bị lỗi ({{ alert.errorRate }}%)</p>
      <div v-if="alert.recentErrors && alert.recentErrors.length" class="mt-2">
        <small><strong>Lỗi gần đây:</strong></small>
        <ul class="mb-1 pl-3">
          <li v-for="(err, i) in alert.recentErrors.slice(0, 5)" :key="i">
            <small>
              {{ err.createTime | moment('HH:mm') }} -
              <span v-if="err.client">{{ err.client.name }}</span>
              <span v-if="err.user"> / {{ err.user.name }}</span>:
              {{ err.question ? err.question.substring(0, 60) : '' }}
              <span class="text-danger" v-if="err.errorMessage"> — {{ err.errorMessage.substring(0, 80) }}</span>
            </small>
          </li>
        </ul>
      </div>
      <b-button size="sm" variant="outline-danger" @click="sendAlertSlack" :disabled="sendingAlert" class="mt-1">
        <font-awesome-icon icon="bell"/> Gửi cảnh báo Slack
      </b-button>
    </b-alert>

    <!-- Summary cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Tổng câu hỏi</h5>
          <h2 class="mb-0">{{ stats.total || 0 }}</h2>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-success mb-1">Thành công</h5>
          <h2 class="mb-0 text-success">{{ stats.success || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.success / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-danger mb-1">Lỗi</h5>
          <h2 class="mb-0 text-danger">{{ stats.error || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.error / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <h5 class="text-warning mb-1">Không dùng tool</h5>
          <h2 class="mb-0 text-warning">{{ stats.noTool || 0 }}</h2>
          <small class="text-muted" v-if="stats.total">{{ ((stats.noTool / stats.total) * 100).toFixed(1) }}%</small>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Thời gian TB</h5>
          <h2 class="mb-0">{{ stats.avgDuration ? (stats.avgDuration / 1000).toFixed(1) + 's' : '—' }}</h2>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Tổng token</h5>
          <h2 class="mb-0">{{ (stats.totalTokens || 0).toLocaleString() }}</h2>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card text-center p-3">
          <h5 class="text-muted mb-1">Chi phí ước tính</h5>
          <h2 class="mb-0">${{ estimatedCost }}</h2>
        </div>
      </div>
    </div>

    <!-- Charts row -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="card p-3">
          <h5>Xu hướng sử dụng</h5>
          <apexchart v-if="trendOptions.xaxis.categories.length" type="bar" height="300" :options="trendOptions" :series="trendSeries"/>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3">
          <h5>Top Tools</h5>
          <div v-if="stats.topTools && stats.topTools.length">
            <div v-for="tool in stats.topTools" :key="tool._id" class="d-flex justify-content-between py-1 border-bottom">
              <span>{{ toolLabels[tool._id] || tool._id }}</span>
              <b-badge variant="primary">{{ tool.count }}</b-badge>
            </div>
          </div>
          <p v-else class="text-muted text-center py-5">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>

    <!-- Top clients -->
    <div class="row">
      <div class="col-md-12">
        <div class="card p-3">
          <h5>Top khách hàng sử dụng</h5>
          <b-table v-if="stats.topClients && stats.topClients.length" :items="stats.topClients" :fields="clientFields" small striped>
            <template #cell(name)="data">
              {{ data.item.name || '—' }}
            </template>
            <template #cell(domain)="data">
              {{ data.item.domain || '—' }}
            </template>
          </b-table>
          <p v-else class="text-muted text-center py-3">Chưa có dữ liệu</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      filterFrom: thirtyDaysAgo.toISOString().split('T')[0],
      filterTo: now.toISOString().split('T')[0],
      stats: {},
      alert: null,
      sendingAlert: false,
      clientFields: [
        {key: 'name', label: 'Khách hàng'},
        {key: 'domain', label: 'Domain'},
        {key: 'count', label: 'Số câu hỏi', class: 'text-right'},
      ],
      toolLabels: {
        search: 'Tìm kiếm',
        report: 'Báo cáo',
        list_my_items: 'Danh sách của tôi',
        create_customer: 'Tạo KH',
        create_ticket: 'Tạo ticket',
        create_task: 'Tạo task',
        create_opportunity: 'Tạo cơ hội',
        update_customer: 'Cập nhật KH',
        update_ticket: 'Cập nhật ticket',
        update_task: 'Cập nhật task',
        update_opportunity: 'Cập nhật cơ hội',
        click_to_call: 'Gọi điện',
        account_info: 'Thông tin TK',
        call_history: 'Lịch sử gọi',
        team_info: 'Thông tin nhóm',
        navigation_guide: 'Hướng dẫn',
        navigation_overview: 'Tổng quan',
        knowledge_base: 'Kiến thức',
        list_automation: 'Xem automation',
        create_automation: 'Tạo automation',
        toggle_automation: 'Bật/tắt automation',
        automation_log: 'Log automation',
        list_report_automation: 'Xem báo cáo tự động',
        create_report_automation: 'Tạo báo cáo tự động',
        toggle_report_automation: 'Bật/tắt báo cáo tự động',
      },
    };
  },
  computed: {
    estimatedCost() {
      // GPT-4o: ~$2.5/1M input, ~$10/1M output
      var prompt = (this.stats.totalPromptTokens || 0) / 1000000 * 2.5;
      var completion = (this.stats.totalCompletionTokens || 0) / 1000000 * 10;
      return (prompt + completion).toFixed(2);
    },
    trendOptions() {
      var trend = (this.stats.trend || []);
      return {
        chart: {type: 'bar', stacked: true, toolbar: {show: false}},
        xaxis: {categories: trend.map(function(t) { return t._id; })},
        colors: ['#28a745', '#dc3545', '#ffc107'],
        plotOptions: {bar: {columnWidth: '60%'}},
      };
    },
    trendSeries() {
      var trend = (this.stats.trend || []);
      return [
        {name: 'Thành công', data: trend.map(function(t) { return t.success; })},
        {name: 'Lỗi', data: trend.map(function(t) { return t.error; })},
        {name: 'Không tool', data: trend.map(function(t) { return t.noTool; })},
      ];
    },
  },
  methods: {
    load() {
      var from = new Date(this.filterFrom).getTime();
      var to = new Date(this.filterTo).getTime() + 24 * 60 * 60 * 1000 - 1;
      this.$service.get('/ai-assistant/stats', {params: {from: from, to: to}})
        .then(res => {
          this.stats = res.data;
        });
      this.checkAlert();
    },
    checkAlert() {
      this.$service.get('/ai-assistant/alert')
        .then(res => {
          this.alert = res.data;
        });
    },
    sendAlertSlack() {
      this.sendingAlert = true;
      this.$service.post('/ai-assistant/alert/notify')
        .then(function () {
          alert('Đã gửi cảnh báo đến Slack!');
        })
        .catch(function () {
          alert('Gửi cảnh báo thất bại');
        })
        .finally(function () {
          this.sendingAlert = false;
        }.bind(this));
    },
  },
  mounted() {
    this.load();
  },
};
</script>
